import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import {
  IngestListResponse,
  PreservationMetrics,
  PreservationTraceResponse,
  QuarantinedIngest,
  RecorridoResponse,
} from '../../../../domain/src/lib/ingest/preservation-trace.model';

/**
 * Trazabilidad / recorrido del archivo. Consume /api/preservation-trace
 * (el interceptor api.vault antepone environment.API_VAULT + auth).
 */
@Injectable({ providedIn: 'root' })
export class PreservationTraceService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);

  private readonly path = '/api/preservation-trace';

  private tenantHeaders(): HttpHeaders {
    const ctx = this.serverContext.getContext();
    let headers = new HttpHeaders();
    if (ctx.tenantId) headers = headers.set('X-Tenant-ID', ctx.tenantId);
    return headers;
  }

  /** Recorrido por las tres partes (incorporación → máster → consulta → actualización). */
  getRecorrido(ingestId: number): Observable<RecorridoResponse> {
    return this.http.get<RecorridoResponse>(`${this.path}/${ingestId}/recorrido`, {
      headers: this.tenantHeaders(),
    });
  }

  /** Trazabilidad completa: estado + eventos PREMIS + fixity + antivirus + copias. */
  getTrace(ingestId: number): Observable<PreservationTraceResponse> {
    return this.http.get<PreservationTraceResponse>(`${this.path}/${ingestId}`, {
      headers: this.tenantHeaders(),
    });
  }

  /** Métricas del tenant (por estado, tasa de cuarentena, objetos, fallos por tipo). */
  getMetrics(): Observable<PreservationMetrics> {
    return this.http.get<PreservationMetrics>(`${this.path}/metrics`, {
      headers: this.tenantHeaders(),
    });
  }

  /** Ingestas en cuarentena del tenant (con motivos), más recientes primero. */
  getQuarantined(limit = 100): Observable<QuarantinedIngest[]> {
    return this.http.get<QuarantinedIngest[]>(`${this.path}/quarantined`, {
      headers: this.tenantHeaders(),
      params: { limit },
    });
  }

  /** Todas las ingestas del tenant (paginado), filtro opcional por estado. */
  getIngests(limit = 50, offset = 0, estado?: string): Observable<IngestListResponse> {
    const params: Record<string, string | number> = { limit, offset };
    if (estado) params['estado'] = estado;
    return this.http.get<IngestListResponse>(this.path, {
      headers: this.tenantHeaders(),
      params,
    });
  }
}
